/**
 * Kanban Board Component
 * 
 * Main board container with columns and drag-and-drop support
 */

import { useState } from 'react';
import { KanbanColumn } from './kanban-column';
import { TaskDetailDrawer } from './task-detail-drawer';
import { UnifiedTask, TaskStatus } from '@/types/tasks';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { TaskBoardColumn } from '@/types/tasks';

interface KanbanBoardProps {
  columns: TaskBoardColumn[];
  onTaskStatusChange: (taskId: string, newStatus: TaskStatus) => void;
  className?: string;
}

export function KanbanBoard({ columns, onTaskStatusChange, className }: KanbanBoardProps) {
  const [selectedTask, setSelectedTask] = useState<UnifiedTask | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [draggedTask, setDraggedTask] = useState<UnifiedTask | null>(null);

  const handleViewTaskDetails = (task: UnifiedTask) => {
    setSelectedTask(task);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    // Delay clearing selected task to allow drawer animation to complete
    setTimeout(() => setSelectedTask(null), 300);
  };

  const handleDragStart = (task: UnifiedTask) => {
    setDraggedTask(task);
  };

  const handleDragEnd = () => {
    setDraggedTask(null);
  };

  const handleDrop = (targetStatus: TaskStatus) => {
    if (draggedTask && draggedTask.status !== targetStatus) {
      onTaskStatusChange(draggedTask.id, targetStatus);
    }
    setDraggedTask(null);
  };

  return (
    <>
      <div className={className}>
        <ScrollArea className="w-full">
          <div className="flex gap-4 pb-4">
            {columns.map(column => (
              <KanbanColumn
                key={column.id}
                column={column}
                onTaskStatusChange={onTaskStatusChange}
                onViewTaskDetails={handleViewTaskDetails}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDrop={handleDrop}
                isDragging={!!draggedTask}
              />
            ))}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </div>

      <TaskDetailDrawer
        task={selectedTask}
        open={drawerOpen}
        onOpenChange={handleDrawerClose}
        onStatusChange={onTaskStatusChange}
      />
    </>
  );
}
